import { useCallback, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { unmaskNumber } from '@givebutter/util';
import { CalculateInstallmentsParams, calculateInstallments } from 'api/pledges';
import { useCurrentPossiblePledge } from 'assets/js/pages/pledges/useCurrentPledge';
import {
    InstallmentFormItem,
    InstallmentStatus,
    PledgeFormValue,
} from 'assets/js/pages/pledges/usePledgeForm';
import { addInstallmentStatus } from 'assets/js/pages/pledges/utils';
import { UseGivebutterFormReturn } from 'components/form/useGivebutterForm';
import { InstallmentFrequency } from 'constants/pledges';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { dateToServerDateTime, isoDateFromServer } from 'helpers/dates';
import { DateTime } from 'luxon';
import { Pledge } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const useInstallmentAutoRecalculate = (methods: UseGivebutterFormReturn<PledgeFormValue>) => {
    const { watch, setValue } = methods;
    const { getInstallments } = useInstallmentCalculation(methods);

    const [
        totalPledge,
        installmentFrequency,
        fulfillmentDate,
        firstInstallmentDate,
        numberOfInstallments,
    ] = watch([
        'amount',
        'installment_frequency',
        'fulfilled_at',
        'first_installment_date',
        'number_of_installments',
    ]);

    useEffect(() => {
        if (!totalPledge) {
            setValue('installments', []);
            return;
        }

        const total = unmaskNumber(totalPledge);

        if (installmentFrequency === InstallmentFrequency.OneTime) {
            setValue('first_installment_date', fulfillmentDate);
            setValue('installments', [
                {
                    date: fulfillmentDate,
                    amount: total,
                },
            ]);
            return;
        }

        if (!firstInstallmentDate || !unmaskNumber(numberOfInstallments ?? 0)) {
            return;
        }

        if (installmentFrequency === InstallmentFrequency.Custom) {
            getInstallments({
                total,
                frequency: InstallmentFrequency.Monthly,
                start_date: dateToServerDateTime(firstInstallmentDate.endOf('day')),
                number_of_installments: numberOfInstallments,
            });
            return;
        }

        getInstallments({
            total,
            frequency: installmentFrequency,
            start_date: firstInstallmentDate
                ? dateToServerDateTime(firstInstallmentDate.endOf('day'))
                : undefined,
            number_of_installments: numberOfInstallments,
        });
    }, [
        totalPledge,
        installmentFrequency,
        firstInstallmentDate,
        numberOfInstallments,
        setValue,
        getInstallments,
        fulfillmentDate,
    ]);
};

const useInstallmentCalculation = (methods: UseGivebutterFormReturn<PledgeFormValue>) => {
    const { accountId } = useCurrentAccount();
    const toast = useToast();
    const { watch, setValue, getValues, clearErrors } = methods;
    const { data: pledge } = useCurrentPossiblePledge();

    const [totalPledge, firstInstallmentDate] = watch(['amount', 'first_installment_date']);

    const { mutate: getInstallments } = useMutation({
        mutationFn: (params: CalculateInstallmentsParams) =>
            calculateInstallments(accountId, params),
        onSuccess: data => {
            const installments = getInstallmentFormItems(
                data.installments.map(item => ({
                    ...item,
                    date: isoDateFromServer(item.date).endOf('day'),
                })),
                pledge?.amount_fulfilled ?? 0,
                getValues('installments'),
            );
            setValue('installments', installments);
            clearErrors(['installments', 'number_of_installments']);
        },
        onError: (e: GivebutterAxiosError) => {
            toast.error(e.response?.data?.message ?? 'An unknown error occurred.');
        },
    });

    const getInstallmentFormItems = useCallback(
        (
            data: { amount: number; date: DateTime }[],
            fulfilledAmount?: number,
            formInstallments?: InstallmentFormItem[],
        ) => {
            return addInstallmentStatus(
                data.map((item, index) => ({
                    ...(formInstallments && formInstallments?.length > 0
                        ? formInstallments[index]
                        : []),
                    ...item,
                })),
                fulfilledAmount,
            );
        },
        [],
    );

    const getDefaultFrequency = useCallback(() => {
        const frequency = getValues('installment_frequency');
        return frequency === InstallmentFrequency.Custom ||
            frequency === InstallmentFrequency.OneTime
            ? InstallmentFrequency.Monthly
            : frequency;
    }, [getValues]);

    const recalculateInstallments = useCallback(() => {
        const installments = getValues('installments');
        getInstallments({
            start_date: dateToServerDateTime(installments[0].date),
            total: unmaskNumber(totalPledge),
            frequency: getDefaultFrequency(),
            number_of_installments: installments.length,
            installments: installments.map(item => ({
                ...item,
                date: dateToServerDateTime(item.date.endOf('day')),
                amount: unmaskNumber(item.amount ?? 0),
                override: item.override || item.status === InstallmentStatus.Paid,
            })),
        });
    }, [getDefaultFrequency, getInstallments, getValues, totalPledge]);

    const appendInstallment = useCallback(() => {
        const installments = getValues('installments');
        getInstallments({
            total: unmaskNumber(totalPledge),
            start_date: dateToServerDateTime(
                (installments[0].date ?? firstInstallmentDate ?? DateTime.now()).endOf('day'),
            ),
            frequency: getDefaultFrequency(),
            number_of_installments: installments.length + 1,
            installments: installments.map(item => ({
                date: dateToServerDateTime(item.date.endOf('day')),
                amount: unmaskNumber(item.amount ?? 0),
                override: item.override || item.status === InstallmentStatus.Paid,
            })),
        });
    }, [firstInstallmentDate, getDefaultFrequency, getInstallments, getValues, totalPledge]);

    const removeInstallment = useCallback(
        (index: number) => {
            const installments = getValues('installments');
            const updatedInstallments = installments
                .filter((_item, i) => i !== index)
                .map(item => ({
                    date: dateToServerDateTime(item.date.endOf('day')),
                    amount: unmaskNumber(item.amount ?? 0),
                    override: item.override || item.status === InstallmentStatus.Paid,
                }));
            getInstallments({
                total: unmaskNumber(totalPledge),
                frequency: getDefaultFrequency(),
                start_date:
                    updatedInstallments[0].date ||
                    dateToServerDateTime(firstInstallmentDate.endOf('day')),
                number_of_installments: installments.length - 1,
                installments: updatedInstallments,
            });
        },
        [firstInstallmentDate, getDefaultFrequency, getInstallments, getValues, totalPledge],
    );

    return {
        getInstallments,
        appendInstallment,
        removeInstallment,
        recalculateInstallments,
        getInstallmentFormItems,
    };
};

const mapInstallmentStatusToPledge = (data: Pledge) => {
    return {
        ...data,
        installments: addInstallmentStatus(
            data.installments.map(item => ({
                ...item,
                date: isoDateFromServer(item.date).endOf('day'),
            })),
            data.amount_fulfilled,
        ),
    };
};

export { useInstallmentCalculation, useInstallmentAutoRecalculate, mapInstallmentStatusToPledge };
